import {
  SpecialNeedsRenderer,
  SpecialNeedsValueGetter,
} from "../../../../../dataGrid/cellRenderer";
import {
  formatDeliveryWindow,
  isValidTimeValue,
} from "../../../../../dataGrid/gridUtilities";
import { COLUMN_CONFIG } from "../../../constants";
import appConstants from "./../../../../../../appConstants";
import {
  DateTimeFilter,
  MultiSelectFilter,
  StaticFilter,
} from "./../../../../../dataGrid/columnFilters";

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = "GridConfiguration";
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration({ columnConfig }) {
    return GridConfig.getDefaultConfiguration({ columnConfig });
  }

  static isFirstColumn(params) {
    const displayedColumns = params.columnApi.getAllDisplayedColumns();
    return displayedColumns[0] === params.column;
  }

  static getAPTWindowConfig(columnConfig) {
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      return {
        headerName: "Delivery Window",
        headerTooltip: "Delivery Window",
        valueGetter: (params) =>
          params.data.shipmentCta || params.data.deliveryWindow.join(","),
        valueFormatter: formatDeliveryWindow,
      };
    }
    return {
      headerName: "APT Window",
      headerTooltip: "Appointment Window",
      valueGetter: (params) => params.data.shipmentCta,
      valueFormatter: (params) => {
        if (!isValidTimeValue(params.value)) {
          return;
        }
        return params.value;
      },
    };
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getDefaultConfiguration({ columnConfig }) {
    const aptConfig = GridConfig.getAPTWindowConfig(columnConfig);

    const defaultConfig = {
      title: appConstants.gridNames.ReturnsGrid,
      keyColumn: "proId",
      rowSelection: "multiple",
      rowDeselection: true,
      defaultColDef: {
        sortable: true,
        resizable: true,
      },
      rowClassRules: {
        "ag-row-disabled": (params) => params.data.isInPlan,
      },
      columns: [
        {
          headerName: "",
          checkboxSelection: true,
          headerCheckboxSelection: true,
          headerClass: "checkBoxHeader",
          minWidth: appConstants.columnWidth.W_62,
          maxWidth: appConstants.columnWidth.W_62,
          cellClassRules: {
            "gridCell-centerAlign": true,
            disabledCheckbox: (params) => params.data.isInPlan,
          },
        },
        {
          headerName: "Pro #",
          field: "proId",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
        },
        {
          headerName: "Spl Needs",
          headerTooltip: "Special Needs",
          minWidth: appConstants.columnWidth.SPL_NEED,
          cellClass: "gridCell-leftAlign",
          valueGetter: SpecialNeedsValueGetter,
          filterFramework: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption,
          },
          cellRendererParams: {
            getAPTWindow: aptConfig.valueGetter,
          },
          cellRendererFramework: SpecialNeedsRenderer,
        },
        {
          headerName: "Curr Term",
          headerTooltip: "Current Terminal",
          field: "currentTerminal",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Next Term",
          headerTooltip: "Next Terminal",
          field: "nextTerminal",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Dest Term",
          headerTooltip: "Destination Terminal",
          field: "destTerminal",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Orig Term",
          headerTooltip: "Origin Terminal",
          field: "originTerminal",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Due Date",
          headerTooltip: "Shipment Due Date",
          field: "shipmentDueDate",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_110,
        },
        {
          headerName: "Customer",
          headerTooltip: "Customer Name",
          field: "customerName",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER,
          tooltipField: "customerName",
        },
        {
          headerName: "Address",
          field: "customerAddress",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER_ADDRESS,
          tooltipField: "customerAddress",
        },
        {
          headerName: "City",
          field: "customerCity",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CITY,
        },
        {
          headerName: "State",
          field: "customerState",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_80,
        },
        {
          headerName: "Zip",
          field: "customerZip",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_85,
        },
        {
          headerName: aptConfig.headerName,
          headerTooltip: aptConfig.headerTooltip,
          field: "shipmentCTA",
          minWidth: appConstants.columnWidth.EARLY_LATE_CTA,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          valueGetter: aptConfig.valueGetter,
          valueFormatter: aptConfig.valueFormatter,
          tooltipValueGetter: (params) => params.value,
        },
        {
          headerName: "Delivery Window",
          headerTooltip: "Delivery Window",
          field: "deliveryWindow",
          minWidth: appConstants.columnWidth.DELIVERY_WINDOW,
          cellClass: "gridCell-leftAlign",
          filterFramework: DateTimeFilter,
          filterParams: {
            DATETYPE: appConstants.DATETYPES.TIME,
            isTimeInterval: true,
            useGetterValue: true,
          },
          valueFormatter: formatDeliveryWindow,
        },
        {
          headerName: "HU",
          field: "hu",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_80,
        },
        {
          headerName: "Weight",
          field: "weight",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_95,
        },
        {
          headerName: "Pieces",
          field: "pieces",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_95,
        },
        {
          headerName: "Status",
          field: "shipmentStatus",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.W_120,
        },
        {
          headerName: "Shipper Name",
          field: "shipperName",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER,
        },
        {
          headerName: "Shipper Address",
          field: "shipperAddress",
          cellClass: "gridCell-leftAlign",
          filterFramework: MultiSelectFilter,
          minWidth: appConstants.columnWidth.CUSTOMER_ADDRESS,
        },
      ],
    };
    if (columnConfig === COLUMN_CONFIG.SAIA) {
      defaultConfig.columns = defaultConfig.columns.filter(
        (column) => column.field !== "deliveryWindow"
      );
    }
    return defaultConfig;
  }
}
